import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Admin } from "../../Admin";
import { Data } from "../../Data";
import { Invoices } from "../../Invoices";
import { KnowledgeCenterRoot } from "../../KnowledgeCenter";
import { OrganizationIndex } from "../../Organization";
import { PersonalPayLinkRoutes } from "../../PersonalPayLink";
import { MembershipBillingHistory } from "../../Settings/screens/MembershipBillingHistory";
import { MembershipPlansWithAddOns } from "../../Settings/screens/MembershipPlansWithAddOns";
import { MembershipTrialSuccess } from "../../Settings/screens/MembershipTrialSuccess";
import { NextGenSubscriptionPlans } from "../../Settings/screens/NextGenSubscriptionPlans";
import { SettingsMain as Settings } from "../../Settings/screens/SettingsMain";
import { NEC1099Filing } from "../../modules/1099NECFiling";
import { BenefitsRoutes } from "../../modules/Benefits";
import { Bookkeeping } from "../../modules/Bookkeeping";
import { BulkImportsIndex } from "../../modules/BulkImporter";
import { BulkReviewIndex } from "../../modules/BulkReview";
import { DashboardIndex } from "../../modules/Dashboard";
import { ENGAGEMENT_TYPES_ROOT_PATH } from "../../modules/EngagementTypes/paths";
import { RouteIndexEngagementTypes } from "../../modules/EngagementTypes/routes";
import { MyResourcesIndex } from "../../modules/MyResources";
import { PATH_MY_RESOURCES } from "../../modules/MyResources/paths";
import { Onboarding } from "../../modules/Onboarding";
import { PayeesIndex } from "../../modules/Payees";
import { PayersIndex } from "../../modules/Payers";
import { PayoutsIndex } from "../../modules/Payouts";
import { PayrollRunsIndex } from "../../modules/PayrollRuns";
import { routeHelperPayrollRuns } from "../../modules/PayrollRuns/paths";
import { PaystubsIndex } from "../../modules/Paystubs";
import { routeHelperPaystubs } from "../../modules/Paystubs/paths";
import { ReportsIndex } from "../../modules/Reports";
import { EngagementRequirementsIndex } from "../../modules/RequirementDefinitions";
import { ENGAGEMENT_REQUIREMENTS_ROOT_PATH } from "../../modules/RequirementDefinitions/paths/constants";
import { StatementIndex } from "../../modules/Statements";
import { TaxDocuments } from "../../modules/TaxDocuments";
import { Tax1099Filing } from "../../modules/TaxFiling";
import { TaxWithholdings } from "../../modules/TaxWithholdings";
import {
  PATH_TAX_WITHHOLDINGS,
  PATH_TAX_WITHHOLDINGS_LEGACY
} from "../../modules/TaxWithholdings/paths";
import { Transfer } from "../../modules/Transfer";
import { PATH_TRANSFER } from "../../modules/Transfer/paths";
import { Wallet } from "../../modules/Wallet";
import { RouteActivateBanking } from "../../modules/Wallet/routes/RouteActivateBanking";
import { WorkLogs } from "../../modules/WorkLogs";
import { routeHelpersWorkLogs } from "../../modules/WorkLogs/paths";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import { PaymentsSecurityScreen } from "../../screens/PaymentsSecurity";
import { GeneralErrorPage } from "../../shared/components/GeneralErrorPage";
import { ModalPayoutAccounts } from "../../shared/components/ModalPayoutAccounts";
import { MemberPlaidModals } from "../../utils/usePlaidLink";
import { BaseAppLayout } from "../BaseAppLayout/BaseAppLayout.component";
import { LoadingPlaceholder } from "../LoadingPlaceholder";
import { OptVerification } from "../OptVerification";
import { SystemLog } from "./SystemLog";
import { useInitMemberApp } from "./useInitMemberApp";

export const MemberApp: React.FC = () => {
  const queryFeatureFlags = useFeatureFlags();

  const [initMemberApp, initMemberAppMeta] = useInitMemberApp();

  // Both sessions are guaranteed to be in place by the time MemberApp mounts
  // (Main holds the loader until v1 and v3 are both ready).
  useEffect(() => {
    initMemberApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store intercomUserJwtAuth experiment flag globally for use in analytics
  useEffect(() => {
    if (queryFeatureFlags.data?.intercomUserJwtAuth !== undefined) {
      window.intercomUserJwtAuthExperiment =
        queryFeatureFlags.data.intercomUserJwtAuth;
    }
  }, [queryFeatureFlags.data?.intercomUserJwtAuth]);

  if (
    initMemberAppMeta.isLoading ||
    initMemberAppMeta.isIdle ||
    !queryFeatureFlags.isFetched
  ) {
    return <LoadingPlaceholder />;
  } else if (initMemberAppMeta.isError) {
    return <GeneralErrorPage />;
  }

  return (
    <React.Fragment>
      <SystemLog />
      <OptVerification />
      <MemberPlaidModals />
      <ModalPayoutAccounts />
      <Switch>
        {/* Fullscreen page routes */}

        <Route path="/member/onboarding" component={Onboarding} />

        <Route
          path="/member/activate-banking"
          component={RouteActivateBanking}
        />

        <Route
          path="/member/:partner/subscription-plans/"
          component={NextGenSubscriptionPlans}
        />
        <Route
          path="/member/membership-plans"
          component={MembershipPlansWithAddOns}
        />
        <Route
          path="/member/subscription/trial-success"
          component={MembershipTrialSuccess}
        />
        <Route
          path="*"
          // eslint-disable-next-line react/no-children-prop
          children={
            <BaseAppLayout>
              <Switch>
                <Route path="/member/statements" component={StatementIndex} />
                <Route
                  path="/member/invoices/payouts"
                  component={PayoutsIndex}
                />

                {/* Nav page routes */}
                <Route path="/member/dashboard" component={DashboardIndex} />

                <Route
                  path={PATH_TAX_WITHHOLDINGS}
                  component={TaxWithholdings}
                />
                <Route
                  path={PATH_TAX_WITHHOLDINGS_LEGACY}
                  render={({ location }) => (
                    <Redirect
                      to={{
                        ...location,
                        pathname: location.pathname.replace(
                          PATH_TAX_WITHHOLDINGS_LEGACY,
                          PATH_TAX_WITHHOLDINGS
                        )
                      }}
                    />
                  )}
                />
                <Route path="/member/tax-documents" component={TaxDocuments} />
                <Route path={PATH_MY_RESOURCES} component={MyResourcesIndex} />
                <Route
                  path={routeHelperPayrollRuns.path}
                  component={PayrollRunsIndex}
                />
                <Route
                  path={routeHelperPaystubs.path}
                  component={PaystubsIndex}
                />

                <Route path="/member/invoices" component={Invoices} />
                <Route
                  path={["/member/imports", "/member/data/batch-imports"]}
                  component={BulkImportsIndex}
                />
                <Route path="/member/data" component={Data} />
                <Route path="/member/1099-filing" component={NEC1099Filing} />
                <Route path="/member/tax-filing" component={Tax1099Filing} />
                <Route path="/member/payees" component={PayeesIndex} />
                <Route
                  path={ENGAGEMENT_TYPES_ROOT_PATH}
                  component={RouteIndexEngagementTypes}
                />
                <Route
                  path={ENGAGEMENT_REQUIREMENTS_ROOT_PATH}
                  component={EngagementRequirementsIndex}
                />

                <Route path="/member/payers" component={PayersIndex} />
                <Route path="/member/bulk-review" component={BulkReviewIndex} />
                <Route path="/member/benefits" component={BenefitsRoutes} />

                <Route path="/member/wallet" component={Wallet} />
                <Route
                  path="/member/organization"
                  component={OrganizationIndex}
                />
                <Route path="/member/bookkeeping" component={Bookkeeping} />
                <Redirect
                  exact
                  from="/member/documents"
                  to="/member/my-resources/general-documents"
                />
                <Route
                  path="/member/knowledge-center"
                  component={KnowledgeCenterRoot}
                />
                <Route path="/member/settings" component={Settings} />
                <Route
                  path="/member/personal-pay-link"
                  component={PersonalPayLinkRoutes}
                />
                <Route
                  path="/member/membership-billing-history"
                  component={MembershipBillingHistory}
                />
                <Route
                  path="/member/payments-security"
                  component={PaymentsSecurityScreen}
                />
                <Route path="/member/reports" component={ReportsIndex} />
                <Route path={PATH_TRANSFER} component={Transfer} />

                <Route path={routeHelpersWorkLogs.path} component={WorkLogs} />

                <Route path="/member/admin" component={Admin} />
                <Redirect from="*" to="/member/dashboard" />
              </Switch>
            </BaseAppLayout>
          }
        />
      </Switch>
    </React.Fragment>
  );
};
